"use client"

import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

const phasesData = [
  { key: "discovery", phase: 1 },
  { key: "design", phase: 2 },
  { key: "build", phase: 3 },
  { key: "operate", phase: 4 }
]

export function ApproachSection() {
  const t = useTranslations("approach")
  
  return (
    <section className="py-24 relative">
      {/* Section header */}
      <div className="text-center mb-16">
        <p className="text-accent-primary text-sm font-medium uppercase tracking-widest mb-3">
          {t("sectionLabel")}
        </p>
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
          {t("title")}
        </h2>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
          {t("subtitle")}
        </p>
      </div>

      {/* Phases */}
      <div className="relative">
        {/* Connecting line */}
        <div className="hidden lg:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-border via-accent-primary/30 to-border" />

        <div className="space-y-12 lg:space-y-0">
          {phasesData.map((phaseData, idx) => {
            const deliverables = t.raw(`phases.${phaseData.key}.deliverables`) as string[]
            
            return (
              <div
                key={phaseData.key}
                className={cn(
                  "relative lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center",
                  idx % 2 === 1 && "lg:flex-row-reverse"
                )}
              >
                {/* Phase number indicator */}
                <div className="hidden lg:flex absolute left-1/2 -translate-x-1/2 w-12 h-12 items-center justify-center">
                  <div className="relative">
                    <div className="absolute inset-0 rounded-full bg-accent-primary/20 animate-pulse" />
                    <div className="relative w-10 h-10 rounded-full bg-card border-2 border-accent-primary flex items-center justify-center">
                      <span className="text-accent-primary font-bold text-sm">
                        {String(phaseData.phase).padStart(2, "0")}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={cn(
                  "lg:pr-20",
                  idx % 2 === 1 && "lg:col-start-2 lg:pr-0 lg:pl-20"
                )}>
                  <div className={cn(
                    "p-6 md:p-8 rounded-2xl border border-border bg-card/30 transition-all duration-300 hover:bg-card/50 hover:border-accent-primary/30",
                    idx % 2 === 0 ? "lg:text-right" : "lg:text-left"
                  )}>
                    {/* Mobile phase number */}
                    <div className="lg:hidden flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-full bg-accent-primary/10 border border-accent-primary/30 flex items-center justify-center">
                        <span className="text-accent-primary font-bold text-xs">
                          {String(phaseData.phase).padStart(2, "0")}
                        </span>
                      </div>
                      <span className="text-xs text-muted-foreground uppercase tracking-wider">
                        {t("phase")} {phaseData.phase}
                      </span>
                    </div>

                    <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3">
                      {t(`phases.${phaseData.key}.title`)}
                    </h3>
                    
                    <p className="text-muted-foreground leading-relaxed mb-6">
                      {t(`phases.${phaseData.key}.description`)}
                    </p>

                    {/* Deliverables */}
                    <div className={cn(
                      "flex flex-wrap gap-2",
                      idx % 2 === 0 ? "lg:justify-end" : "lg:justify-start"
                    )}>
                      {deliverables.map((deliverable: string, dIdx: number) => (
                        <span
                          key={dIdx}
                          className="inline-flex items-center px-3 py-1 rounded-full bg-accent-primary/10 text-accent-primary text-xs font-medium"
                        >
                          {deliverable}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Empty column for alternating layout */}
                <div className={cn(
                  "hidden lg:block",
                  idx % 2 === 1 && "lg:col-start-1 lg:row-start-1"
                )} />
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
